import {
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonPage,
  IonRow,
  IonToolbar,
} from '@ionic/react';
import { useFormikContext } from 'formik';
import { ReactElement, ReactNode } from 'react';
import { useHistory, useParams } from 'react-router';
import { PermissionType } from '../../api/graphql/generated';
import { useRoleFormContext } from '../../api/hooks/role.hooks';
import { RightAlignedButtons } from '../../components/button/aligned-buttons';
import { BaseButton } from '../../components/button/base-button';
import { FormSubmitButton } from '../../components/form/form-submit.button';
import { NestedPageHeader } from '../../components/header/page.header';
import { RolePermissionFormFieldInput } from '../../components/role/role-permission-form-field-input';
import {
  EntityToLocalizedText,
  RolePermissionForm,
} from '../../components/role/role.utils';
import { CenteredContent } from '../../components/ui/centered-content';
import i18n from '../../i18n';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { useBeaconRouter } from '../../route/route.hooks';
import { toTitleCase } from '../../scratchpad/application/shared/util/string';

interface RoleEntityPermissionsRouteParams {
  id: string;
  entity: string;
}

const RoleEntityPermissionsPage = (): ReactElement => {
  const { entity, id } = useParams<RoleEntityPermissionsRouteParams>();

  const { builder } = useBeaconRouter();
  const selectedEntityBackPath = builder.getAdminRoleRoute(id);
  const selectedPermissionTitle = getLocalizedText(
    EntityToLocalizedText[entity as PermissionType]
  );

  const title = selectedPermissionTitle
    ? i18n.t('common:role_entity_permissions', {
        entityName: toTitleCase(selectedPermissionTitle),
      })
    : i18n.t('common:customize_role');

  return (
    <IonPage>
      <NestedPageHeader
        title={title}
        defaultBackRoute={selectedEntityBackPath}
      />
      <RoleEntityPermissions entity={entity} />
    </IonPage>
  );
};

interface RoleEntityPermissionsProps {
  entity: string;
}

const RoleEntityPermissions = (
  props: RoleEntityPermissionsProps
): ReactNode => {
  const { entity } = props;
  const { sortedRolePermissionFormFields, isReplacingRolePermissions } =
    useRoleFormContext();
  const { isValid, dirty, submitForm, resetForm } =
    useFormikContext<RolePermissionForm>();
  const { replace } = useHistory();
  const { builder } = useBeaconRouter();

  if (!entity) {
    return (
      <IonContent>
        <CenteredContent>
          <IonGrid className="ion-text-center">
            <IonRow>
              <IonCol>
                {i18n.t('common:role_permissions_failed_to_load')}
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <BaseButton
                  buttonProps={{
                    onClick: () => replace(builder.getAdminRolesRoute()),
                  }}
                >
                  {i18n.t('common:go_to_page', {
                    pageName: i18n.t('common:roles'),
                  })}
                </BaseButton>
              </IonCol>
            </IonRow>
          </IonGrid>
        </CenteredContent>
      </IonContent>
    );
  }

  const formFields = sortedRolePermissionFormFields[entity] ?? [];

  const formFieldInputs = formFields?.map((field) => {
    return (
      <RolePermissionFormFieldInput key={field.template.id} field={field} />
    );
  });

  const isSubmitDisabled = !isValid || !dirty;

  return (
    <>
      <IonContent>{formFieldInputs}</IonContent>
      <IonFooter>
        <IonToolbar>
          <RightAlignedButtons>
            <BaseButton
              buttonProps={{
                color: 'danger',
                disabled: !dirty,
                onClick: () => {
                  resetForm();
                },
              }}
            >
              {getLocalizedText('common:discard_changes')}
            </BaseButton>
            <FormSubmitButton
              disabled={isSubmitDisabled}
              loading={isReplacingRolePermissions}
              label={i18n.t('common:save')}
              buttonProps={{
                onClick: submitForm,
              }}
            />
          </RightAlignedButtons>
        </IonToolbar>
      </IonFooter>
    </>
  );
};

export { RoleEntityPermissionsPage };
